import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { ATTRIBUTION_WINDOW_DAYS, PRIORITY_RANK, type AttributionSource } from "./attributionTypes";

const TouchSchema = z.object({
  sessionId: z.string().min(8).max(128),
  userId: z.string().uuid().nullish(),
  source: z.enum([
    "referral_code", "qr", "partner_link", "campaign",
    "tenant", "sportsbook", "influencer", "unknown",
  ]),
  partnerId: z.string().max(120).nullish(),
  influencerId: z.string().max(120).nullish(),
  campaignId: z.string().max(120).nullish(),
  tenantId: z.string().max(120).nullish(),
  referralCode: z.string().max(64).nullish(),
  qrId: z.string().max(64).nullish(),
  sportsbookPartner: z.string().max(64).nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
});

/** Capture / merge an attribution touch with priority + reactivation logic. */
export const captureAttributionTouch = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => TouchSchema.parse(data))
  .handler(async ({ data }) => {
    const sb = supabaseAdmin;
    const incomingRank = PRIORITY_RANK[data.source as AttributionSource] ?? 90;
    const nowIso = new Date().toISOString();
    const expiresIso = new Date(
      Date.now() + ATTRIBUTION_WINDOW_DAYS * 86400 * 1000,
    ).toISOString();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: existing } = await (sb.from("partner_attribution") as any)
      .select("id, attribution_status, priority_rank, partner_id, campaign_id")
      .eq("session_id", data.sessionId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const baseRow = {
      session_id: data.sessionId,
      user_id: data.userId ?? null,
      partner_id: data.partnerId ?? null,
      influencer_id: data.influencerId ?? null,
      campaign_id: data.campaignId ?? null,
      tenant_id: data.tenantId ?? null,
      referral_code: data.referralCode ?? null,
      qr_id: data.qrId ?? null,
      sportsbook_partner: data.sportsbookPartner ?? null,
      source: data.source,
      priority_rank: incomingRank,
      last_touch_at: nowIso,
      last_active_at: nowIso,
      expires_at: expiresIso,
      attribution_status: "active",
      metadata: data.metadata ?? {},
    };

    if (!existing) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: inserted } = await (sb.from("partner_attribution") as any)
        .insert({ ...baseRow, first_touch_at: nowIso })
        .select("id")
        .maybeSingle();
      await logEvent(sb, inserted?.id ?? null, data.sessionId, data.userId ?? null,
        data.partnerId ?? null, data.campaignId ?? null, data.tenantId ?? null,
        "attribution_created", { source: data.source });
      return { id: inserted?.id, action: "created" as const };
    }

    const expired = existing.attribution_status !== "active";
    const higher = incomingRank < (existing.priority_rank ?? 90);

    if (expired || higher) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (sb.from("partner_attribution") as any)
        .update(baseRow)
        .eq("id", existing.id);
      await logEvent(sb, existing.id, data.sessionId, data.userId ?? null,
        data.partnerId ?? null, data.campaignId ?? null, data.tenantId ?? null,
        expired ? "attribution_reactivated" : "attribution_transferred",
        { from_partner: existing.partner_id, from_campaign: existing.campaign_id, source: data.source });
      return { id: existing.id, action: expired ? "reactivated" as const : "transferred" as const };
    }

    // Same/lower priority on active record → just touch last_active_at + expiry
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (sb.from("partner_attribution") as any)
      .update({ last_active_at: nowIso, expires_at: expiresIso, user_id: data.userId ?? null })
      .eq("id", existing.id);
    return { id: existing.id, action: "refreshed" as const };
  });

const EventSchema = z.object({
  sessionId: z.string().min(8).max(128),
  userId: z.string().uuid().nullish(),
  eventType: z.enum([
    "signup", "first_deposit", "deposit", "betting_activity",
    "engagement", "sponsor_interaction", "qr_scan", "campaign_entry",
    "creator_referral",
  ]),
  eventData: z.record(z.string(), z.unknown()).nullish(),
});

/** Record a conversion / engagement event against current attribution. */
export const recordAttributionEvent = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => EventSchema.parse(data))
  .handler(async ({ data }) => {
    const sb = supabaseAdmin;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: attr } = await (sb.from("partner_attribution") as any)
      .select("id, partner_id, campaign_id, tenant_id, conversion_status, deposit_status, betting_status")
      .eq("session_id", data.sessionId)
      .eq("attribution_status", "active")
      .order("last_active_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const patch: Record<string, string> = { last_active_at: new Date().toISOString() };
    if (data.eventType === "signup") patch.conversion_status = "signup";
    if (data.eventType === "engagement" && attr?.conversion_status === "signup") {
      patch.conversion_status = "engaged";
    }
    if (data.eventType === "first_deposit") {
      patch.deposit_status = "first_deposit";
      patch.conversion_status = "converted";
    }
    if (data.eventType === "deposit" && attr?.deposit_status === "first_deposit") {
      patch.deposit_status = "recurring";
    }
    if (data.eventType === "betting_activity") {
      patch.betting_status = attr?.betting_status === "placed_bet" ? "active_bettor" : "placed_bet";
    }

    if (attr?.id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (sb.from("partner_attribution") as any).update(patch).eq("id", attr.id);
    }
    await logEvent(sb, attr?.id ?? null, data.sessionId, data.userId ?? null,
      attr?.partner_id ?? null, attr?.campaign_id ?? null, attr?.tenant_id ?? null,
      data.eventType, data.eventData ?? {});
    return { ok: true, attributionId: attr?.id ?? null };
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function logEvent(sb: any, attributionId: string | null, sessionId: string,
  userId: string | null, partnerId: string | null, campaignId: string | null,
  tenantId: string | null, eventType: string, eventData: Record<string, unknown>) {
  await sb.from("partner_attribution_events").insert({
    attribution_id: attributionId,
    session_id: sessionId,
    user_id: userId,
    partner_id: partnerId,
    campaign_id: campaignId,
    tenant_id: tenantId,
    event_type: eventType,
    event_data: eventData,
  });
}